import { usePreviousPage, useReduxState } from 'hooks';
import { User } from 'components/common';
import { Page, PageHeader, PageContent, PageNav } from 'components/page';

interface Props {
  activeLink?: string;
  children: React.ReactNode;
  hideNavBar?: boolean;
  withSidebar?: boolean;
}

const UserPage = ({
  activeLink = '',
  children,
  hideNavBar = false,
  withSidebar
}: Props) => {
  const {
    messages,
    pageState: { user },
    serverConfig,
    user: currentUser
  } = useReduxState();

  const backLink = usePreviousPage({ fallbackUrl: '/users' });

  const navActiveLink = `/users/${user?.id}${activeLink}`;

  const navLinks = [
    {
      as: `/users/${user?.id}`,
      href: '/users/[userId]',
      icon: 'user' as const,
      title: messages.commonOverview
    },
    ...(serverConfig.hideCoursesFromUI
      ? []
      : [
          {
            as: `/users/${user?.id}/courses`,
            href: '/users/[userId]/courses',
            icon: 'book' as const,
            title: messages.commonCourses
          }
        ]),
    ...(serverConfig.hidePathsFromUI
      ? []
      : [
          {
            as: `/users/${user?.id}/paths`,
            href: '/users/[userId]/paths',
            icon: 'tasks' as const,
            title: messages.paths
          }
        ]),
    ...(serverConfig.hideAchievementsFromUI
      ? []
      : [
          {
            as: `/users/${user?.id}/achievements`,
            href: '/users/[userId]/achievements',
            icon: 'check-circle' as const,
            title: messages.commonAchievementsAlt
          }
        ]),
    ...(serverConfig.featureFlags.enableCompetencyImprovements &&
    !user?.roles?.includes('guest')
      ? [
          {
            as: `/users/${user?.id}/competencies`,
            href: '/users/[userId]/competencies',
            icon: 'medal' as const,
            title: messages.commonCompetencies
          }
        ]
      : []),
    ...(serverConfig.platformCompetenciesOnly
      ? []
      : [
          {
            as: `/users/${user?.id}/completions`,
            href: '/users/[userId]/completions',
            icon: 'history' as const,
            title: messages.profileCompletionHistory
          }
        ]),
    ...(currentUser?.roles.includes('vuoadmin') &&
    !serverConfig.hideCoursesFromUI
      ? [
          {
            as: `/users/${user?.id}/feedbacks`,
            href: '/users/[userId]/feedbacks',
            icon: 'bullhorn' as const,
            title: messages.profileFeedbacks
          }
        ]
      : []),
    ...(currentUser?.roles.includes('superadmin')
      ? [
          {
            as: `/users/${user?.id}/events`,
            href: '/users/[userId]/events',
            icon: 'list' as const,
            title: messages.commonLogs
          }
        ]
      : [])
  ];

  return (
    <Page activePathname="/users">
      {!user || !currentUser ? null : (
        <>
          <PageHeader
            backLinkAs={backLink.as}
            backLinkHref={backLink.href}
            title={backLink.title}
          />
          {!hideNavBar && (
            <PageNav activeLink={navActiveLink} links={navLinks}>
              <User user={user} />
            </PageNav>
          )}
          <PageContent withSidebar={withSidebar}>{children}</PageContent>
        </>
      )}
    </Page>
  );
};

export default UserPage;
